import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { UpgradeLimitModal, useSession } from '@ltvco/refresh-lib/v2';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

const USER_IN_RECYCLING_KEY = 'userInRecycling';
const NONCOMPLIANT_USER_KEY = 'noncompliantUser';
const RECYCLING_DELAY_MS = 500;

export function UpgradeLimitModalGate() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isReactivationPath =
    pathname === '/reactivation' || pathname.startsWith('/reactivation/');
  const { session } = useSession();
  const {
    accountLoaded,
    isLoading,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
  } = useLimitedPlanInfo();
  const [upgradeLimitModalShown, setUpgradeLimitModalShown] =
    useState<boolean>(false);
  const hasValidReportLimit = reportLimit > 0 && reportsRun >= 0;
  const remainingReports = hasValidReportLimit ? reportLimit - reportsRun : 0;
  const hasReachedNinetyPercent = reportsRunPercentage >= 90;
  const alertAlreadyShown =
    localStorage.getItem('upgradeLimitModalShown') === 'true';
  const shouldShowAlert =
    accountLoaded &&
    !isLoading &&
    hasValidReportLimit &&
    hasReachedNinetyPercent &&
    !alertAlreadyShown;

  const redirectToUpgradePlan = () => {
    localStorage.setItem('upgradeLimitModalShown', 'true');
    setUpgradeLimitModalShown(false);
    navigate('/upgrade/plan');
  };

  useEffect(() => {
    if (isReactivationPath) {
      setUpgradeLimitModalShown(false);
      return;
    }
    if (shouldShowAlert) {
      setUpgradeLimitModalShown(true);
    } else {
      setUpgradeLimitModalShown(false);
    }
  }, [shouldShowAlert, isReactivationPath]);

  const prevAtNinetyPercentRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!accountLoaded) {
      return;
    }

    if (!hasValidReportLimit) {
      prevAtNinetyPercentRef.current = null;
      if (localStorage.getItem('upgradeLimitModalShown') === 'true') {
        localStorage.removeItem('upgradeLimitModalShown');
      }
      return;
    }

    const wasAtNinetyPercent = prevAtNinetyPercentRef.current;
    prevAtNinetyPercentRef.current = hasReachedNinetyPercent;

    if (wasAtNinetyPercent === true && !hasReachedNinetyPercent) {
      if (localStorage.getItem('upgradeLimitModalShown') === 'true') {
        localStorage.removeItem('upgradeLimitModalShown');
      }
    }
  }, [accountLoaded, hasValidReportLimit, hasReachedNinetyPercent]);

  const account = session?.account;
  const userInRecycling =
    account?.account?.user_info?.user_in_recycling ?? false;
  const [recyclingBlocked, setRecyclingBlocked] = useState(() => {
    return (
      userInRecycling || localStorage.getItem(USER_IN_RECYCLING_KEY) === 'true'
    );
  });

  useEffect(() => {
    if (userInRecycling) {
      localStorage.setItem(USER_IN_RECYCLING_KEY, 'true');
      setRecyclingBlocked(true);
      return;
    }
    localStorage.removeItem(USER_IN_RECYCLING_KEY);
    const id = setTimeout(() => setRecyclingBlocked(false), RECYCLING_DELAY_MS);
    return () => clearTimeout(id);
  }, [userInRecycling]);

  const isNoncompliantUser = account?.account?.user_info?.noncompliant;
  const [noncompliantBlocked, setNoncompliantBlocked] = useState(() => {
    // Block until we have an explicit false — covers both the noncompliant case
    // and the race where compliance data hasn't loaded yet.
    return (
      isNoncompliantUser !== false ||
      localStorage.getItem(NONCOMPLIANT_USER_KEY) === 'true'
    );
  });

  useEffect(() => {
    if (isNoncompliantUser === true) {
      localStorage.setItem(NONCOMPLIANT_USER_KEY, 'true');
      setNoncompliantBlocked(true);
      return;
    }

    if (isNoncompliantUser === undefined) {
      // Status unknown — keep blocked until resolved.
      setNoncompliantBlocked(true);
      return;
    }

    // isNoncompliantUser === false: explicitly compliant, safe to unblock.
    localStorage.removeItem(NONCOMPLIANT_USER_KEY);
    const id = setTimeout(
      () => setNoncompliantBlocked(false),
      RECYCLING_DELAY_MS
    );
    return () => clearTimeout(id);
  }, [isNoncompliantUser]);

  const { vinVariation, fraudScanOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const planUniqueName =
    account?.account?.subscription_info?.subscription_plan_unique_name ?? '';
  const isInternationalSpanishPlan =
    constants?.internationalSpanishPlanNames?.includes(planUniqueName) ?? false;
  const hideUpgradeModal =
    vinVariation || isInternationalSpanishPlan || fraudScanOnlyVariation;

  if (
    hideUpgradeModal ||
    userInRecycling ||
    recyclingBlocked ||
    isNoncompliantUser ||
    noncompliantBlocked
  ) {
    return null;
  }

  return (
    <UpgradeLimitModal
      firstName={account?.account?.user_info?.first_name ?? ''}
      lastName={account?.account?.user_info?.last_name ?? ''}
      remainingReports={remainingReports}
      isOpen={upgradeLimitModalShown && !isReactivationPath}
      onClose={() => {
        localStorage.setItem('upgradeLimitModalShown', 'true');
        setUpgradeLimitModalShown(false);
      }}
      onClick={redirectToUpgradePlan}
    />
  );
}
